import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import * as _ from 'lodash';
import {BzMenuItem, BzSelectOption} from '@bringoz/ui';
import {
  bzIconAgent,
  bzIconBranch,
  bzIconChevronLeft,
  bzIconChevronRight,
  bzIconExportXsl,
  bzIconGroup,
  bzIconSettings,
  bzIconTagOutline
} from '@bringoz/ui/icons';
import {BringozDateRange} from '../../../../commons/angular/models/bringoz-date-range.model';
import {injectLegacy} from '../../../../commons/angular/utils/inject-legacy.util';
import type {DateRangeTimeFiltersService} from '../../../../commons/services/date-range-time-filters.service';
import {TimeCardStatusType} from '../../../types/timecard-status.type';
import {
  TimekeepingDateRange,
  TimekeepingFilterOption,
  TimekeepingListFilters
} from '../../models/timekeeping-list.model';

const STATUSES: string[] = Object.values(TimeCardStatusType);

enum TimekeepingExportType {
  Timecards = 'timecards',
  Mileage = 'mileage',
  Payroll = 'payroll',
  MondelezSummary = 'mondelezSummary'
}

const EXPORT_LABEL_KEYS: Record<TimekeepingExportType, string> = {
  [TimekeepingExportType.Timecards]: 'Export Timecards',
  [TimekeepingExportType.Mileage]: 'Export Mileage',
  [TimekeepingExportType.Payroll]: 'Payroll',
  [TimekeepingExportType.MondelezSummary]: 'Generate Mondelez Summary Report'
};
const STATUS_TRANSLATION_KEYS: string[] = _.map(STATUSES, (status) => `TIMECARD_SHIFT_STATUS.${status}`);

require('./timekeeping-toolbar.scss');
@Component({
  standalone: false,
  selector: 'app-timekeeping-toolbar',
  templateUrl: './timekeeping-toolbar.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimekeepingToolbarComponent {

  readonly searchQuery = input<string>('');
  readonly filters = input<TimekeepingListFilters>();
  readonly agentTagOptions = input<TimekeepingFilterOption[]>([]);
  readonly groupTagOptions = input<TimekeepingFilterOption[]>([]);
  readonly groupOptions = input<TimekeepingFilterOption[]>([]);
  readonly branchOptions = input<TimekeepingFilterOption[]>([]);
  readonly selectedStatuses = input<string[]>([]);
  readonly range = input<TimekeepingDateRange>();
  readonly exporting = input<boolean>(false);
  readonly exportingPayroll = input<boolean>(false);
  readonly completeDisabled = input<boolean>(true);

  readonly searchChange = output<string>();
  readonly agentTagsChange = output<string[]>();
  readonly groupTagsChange = output<string[]>();
  readonly groupIdsChange = output<string[]>();
  readonly branchIdsChange = output<string[]>();
  readonly statusesChange = output<string[]>();
  readonly rangeChange = output<TimekeepingDateRange>();
  readonly browseWeek = output<boolean>();
  readonly exportTimecards = output<void>();
  readonly exportMileage = output<void>();
  readonly exportPayroll = output<void>();
  readonly exportMondelezSummary = output<void>();
  readonly openSettings = output<MouseEvent>();
  readonly completeTimecards = output<void>();

  readonly customTemplates: BringozDateRange[] = injectLegacy<DateRangeTimeFiltersService>('dateRangeTimeFiltersService')
    .getTimekeepingCustomTemplates();
  /** Same pause as the previous search input, so typing doesn't fire a request per key. */
  readonly searchDebounceMs = 1000;

  readonly agentTagsIcon = bzIconAgent;
  readonly groupTagsIcon = bzIconTagOutline;
  readonly groupsIcon = bzIconGroup;
  readonly branchesIcon = bzIconBranch;
  readonly chevronLeftIcon = bzIconChevronLeft;
  readonly chevronRightIcon = bzIconChevronRight;
  readonly exportXslIcon = bzIconExportXsl;
  readonly settingsIcon = bzIconSettings;

  private readonly statusTranslations = toSignal(
    inject(TranslateService).stream(STATUS_TRANSLATION_KEYS) as Observable<Record<string, string>>,
    {initialValue: {} as Record<string, string>}
  );

  readonly statusOptions = computed<BzSelectOption<string>[]>(() => {
    const translations = this.statusTranslations();
    return _.map(STATUSES, (status, index) => ({value: status, label: translations[STATUS_TRANSLATION_KEYS[index]]}));
  });

  private readonly exportLabels = toSignal(
    inject(TranslateService).stream(Object.values(EXPORT_LABEL_KEYS)) as Observable<Record<string, string>>,
    {initialValue: {} as Record<string, string>}
  );

  readonly exportMenuItems = computed<BzMenuItem[]>(() => {
    const labels = this.exportLabels();
    const exportingPayroll = this.exportingPayroll();
    return _.map(EXPORT_LABEL_KEYS, (key, id) => ({
      id,
      label: labels[key] || key,
      disabled: id === TimekeepingExportType.Payroll && exportingPayroll
    }));
  });

  onExportSelected(item: BzMenuItem) {
    switch (item.id) {
      case TimekeepingExportType.Timecards:
        this.exportTimecards.emit();
        break;
      case TimekeepingExportType.Mileage:
        this.exportMileage.emit();
        break;
      case TimekeepingExportType.Payroll:
        this.exportPayroll.emit();
        break;
      case TimekeepingExportType.MondelezSummary:
        this.exportMondelezSummary.emit();
        break;
    }
  }

  onStatusesSelect(value: string | readonly string[] | null) {
    this.statusesChange.emit(value ? _.castArray(value) as string[] : []);
  }

  onRangeSelect(range: BringozDateRange) {
    this.rangeChange.emit({dateStart: range.dateStart, dateEnd: range.dateEnd});
  }
}
